import { ConfirmDialog } from "@/components/ConfirmDialog";
import { InlineError, LoadingIndicator } from "@/components/QueryState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, type ContainerRow, type Permissions } from "@/lib/api";
import { NO_SCOPES, sameChoice, type Choices } from "@/lib/capability-access";
import type { ConnectionStatus } from "@/lib/connection-status";
import {
  choicesDiffer,
  GOOGLE_WORKSPACE_CAPABILITIES,
  grantedChoices,
  scopesFor,
} from "@/lib/google-workspace-access";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { useMemo, useState } from "react";
import { CapabilityList, CapabilityRow } from "./CapabilityRows";
import {
  ConnectionHeader,
  ConnectionSection,
  DetailList,
  DetailRow,
  DisconnectButton,
  Notice,
  StatusLine,
} from "./ConnectionParts";
import type { ConnectionDef } from "./connections-lib";

// Google Workspace through the org's own OAuth client in its Google Cloud
// project: pick what Boo can use, then authorize as a Google account. A legacy
// path the hub catalog superseded; Connections lists it only where it was set
// up.
export function GoogleWorkspaceConnection({
  def,
  container,
  permissions,
}: {
  def: ConnectionDef;
  container: ContainerRow;
  permissions: Permissions;
}) {
  const queryClient = useQueryClient();
  const running = container.deploymentStatus === "running";
  const stateKey = ["gchat-workspace-admin-state", container.id];
  const visibilityKey = ["gchat-workspace-visibility", container.id];
  const statusQuery = useQuery({
    queryKey: stateKey,
    queryFn: () => api.gchat.custom.admin.state(container.id),
    enabled: running,
  });
  const data = statusQuery.data;
  const visibilityQuery = useQuery({
    queryKey: visibilityKey,
    queryFn: () => api.gchat.custom.admin.visibility(container.id),
    enabled: running && Boolean(data?.connected),
  });

  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const disconnect = useMutation({
    mutationFn: () => api.gchat.custom.admin.disconnect(container.id),
    onSuccess: () => {
      setDisconnectOpen(false);
      queryClient.invalidateQueries({ queryKey: stateKey });
      queryClient.invalidateQueries({ queryKey: visibilityKey });
    },
  });

  const granted = data?.grantedScopes ?? NO_SCOPES;
  const baseline = useMemo(() => grantedChoices(granted), [granted]);
  // null = follow what's granted; set once the viewer changes something.
  const [draft, setDraft] = useState<Choices | null>(null);
  const choices = draft ?? baseline;
  const canManage = permissions?.canConnectGchat !== false;
  const connected = data?.connected === true;
  const picked = scopesFor(choices);
  const dirty = connected && choicesDiffer(choices, baseline);

  const status: ConnectionStatus = !data
    ? { loading: statusQuery.isLoading, state: "off", label: running ? "Checking…" : "Not available" }
    : connected
      ? data.status === "needs_reauth"
        ? { loading: false, state: "elsewhere", label: "Needs authorizing again" }
        : { loading: false, state: "connected", label: `Connected as ${data.connectedAs ?? "a Google account"}` }
      : { loading: false, state: "off", label: data.clientConfigured ? "Not authorized" : "Not set up" };

  const header = (
    <ConnectionHeader
      def={def}
      status={<StatusLine status={status} className="h-5 text-sm" />}
      actions={
        connected &&
        canManage && <DisconnectButton pending={disconnect.isPending} onClick={() => setDisconnectOpen(true)} />
      }
    />
  );

  const authorize = () => {
    if (!data?.installUrlBase) return;
    const returnTo = window.location.href;
    window.location.href = `${data.installUrlBase}?scopes=${encodeURIComponent(picked.join(" "))}&returnTo=${encodeURIComponent(returnTo)}`;
  };

  let body;
  if (!running) {
    body = (
      <Notice>
        <p>Google Workspace can be managed while this Boo is running.</p>
      </Notice>
    );
  } else if (statusQuery.isLoading) {
    body = <LoadingIndicator message="Checking Google Workspace…" />;
  } else if (statusQuery.isError || !data) {
    body = <InlineError error={statusQuery.error ?? "Failed to check Google Workspace"} />;
  } else if (data.agentOffline) {
    body = (
      <Notice>
        <p>Boo is offline. Google Workspace access can be managed once it&apos;s back.</p>
      </Notice>
    );
  } else if (data.featureUnavailable) {
    body = (
      <Notice>
        <p>Google Workspace access isn&apos;t ready on this Boo just yet.</p>
      </Notice>
    );
  } else if (!data.clientConfigured) {
    body = <OAuthClientSetup containerId={container.id} redirectUri={data.redirectUri} canManage={canManage} />;
  } else {
    body = (
      <>
        {data.status === "needs_reauth" && (
          <Notice tone="warning">
            <p>Google rejected Boo&apos;s access. Authorize again below.</p>
          </Notice>
        )}
        {connected && (
          <DetailList>
            <DetailRow term="Signed in as">{data.connectedAs ?? "Unknown account"}</DetailRow>
            {data.lastRefreshedAt && (
              <DetailRow term="Last refreshed">{new Date(data.lastRefreshedAt).toLocaleString()}</DetailRow>
            )}
          </DetailList>
        )}

        <ConnectionSection
          title="What Boo can use"
          description={
            !canManage
              ? "Only this Boo's admins can change this."
              : connected
                ? "Changes apply once you authorize again."
                : "Turn on what Boo needs, then authorize."
          }
        >
          <CapabilityList>
            {GOOGLE_WORKSPACE_CAPABILITIES.map((cap) => (
              <CapabilityRow
                key={cap.id}
                idPrefix="gworkspace"
                cap={cap}
                icon={
                  cap.iconUrl ? (
                    <img src={cap.iconUrl} alt="" className="size-5" />
                  ) : (
                    <span className="text-xs font-semibold text-muted-foreground">{cap.name.slice(0, 2)}</span>
                  )
                }
                choice={choices[cap.id]!}
                pending={connected && !sameChoice(choices[cap.id]!, baseline[cap.id]!)}
                editable={canManage}
                onChange={(next) => setDraft({ ...choices, [cap.id]: next })}
              />
            ))}
          </CapabilityList>
          {dirty && (
            <Button size="sm" variant="ghost" className="-ml-2 text-muted-foreground" onClick={() => setDraft(null)}>
              Undo changes
            </Button>
          )}
        </ConnectionSection>

        {canManage && (!connected || dirty || data.status === "needs_reauth") && (
          <ConnectionSection
            title={connected ? "Apply your changes" : "Authorize"}
            description={
              picked.length === 0
                ? connected
                  ? "To remove all access, disconnect instead."
                  : "Turn on at least one of the above to continue."
                : "Sign in with the Google account Boo should use."
            }
          >
            {!connected && data.redirectUri && (
              <p className="text-xs text-muted-foreground">
                First add this URL to your OAuth client&apos;s <em>Authorized redirect URIs</em>:{" "}
                <span className="font-mono break-all">{data.redirectUri}</span>
              </p>
            )}
            <Button
              className="w-fit gap-1.5"
              disabled={picked.length === 0 || !data.installUrlBase}
              onClick={authorize}
            >
              <ArrowUpRight className="size-3.5" />
              {connected ? "Authorize again" : "Authorize with Google"}
            </Button>
          </ConnectionSection>
        )}

        {connected && visibilityQuery.data && <WhatBooCanSee visibility={visibilityQuery.data} />}
      </>
    );
  }

  return (
    <>
      {header}
      {body}
      {disconnect.isError && <InlineError error={disconnect.error} />}
      <ConfirmDialog
        open={disconnectOpen}
        onOpenChange={setDisconnectOpen}
        title="Disconnect Google Workspace?"
        description="Boo stops using Gmail, Drive, Calendar, and the rest through this account. You can authorize again anytime."
        confirmLabel="Disconnect"
        loading={disconnect.isPending}
        onConfirm={() => disconnect.mutate()}
      />
    </>
  );
}

// First-time setup: the OAuth 2.0 Web Client every Google app Boo uses here
// goes through.
function OAuthClientSetup({
  containerId,
  redirectUri,
  canManage,
}: {
  containerId: string;
  redirectUri: string | null;
  canManage: boolean;
}) {
  const queryClient = useQueryClient();
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const save = useMutation({
    mutationFn: () => api.gchat.custom.userOauth.saveClient(containerId, clientId.trim(), clientSecret.trim()),
    onSuccess: () => {
      setClientSecret("");
      queryClient.invalidateQueries({ queryKey: ["gchat-workspace-admin-state", containerId] });
      queryClient.invalidateQueries({ queryKey: ["gchat-custom-user-oauth-status", containerId] });
    },
  });
  return (
    <ConnectionSection
      title="OAuth client"
      description="Create an OAuth 2.0 Web Client in your Google Cloud project and paste its credentials. The same client serves every Google app Boo uses here."
    >
      {redirectUri && (
        <p className="text-xs text-muted-foreground">
          Add this URL to the client&apos;s <em>Authorized redirect URIs</em> in Google Cloud Console:{" "}
          <span className="font-mono break-all">{redirectUri}</span>
        </p>
      )}
      {canManage && (
        <form
          className="space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor={`workspace-oauth-client-id-${containerId}`} className="text-xs">
                OAuth client ID
              </Label>
              <Input
                id={`workspace-oauth-client-id-${containerId}`}
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                placeholder="123-abc.apps.googleusercontent.com"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor={`workspace-oauth-client-secret-${containerId}`} className="text-xs">
                OAuth client secret
              </Label>
              <Input
                id={`workspace-oauth-client-secret-${containerId}`}
                type="password"
                value={clientSecret}
                onChange={(e) => setClientSecret(e.target.value)}
                placeholder="GOCSPX-..."
              />
            </div>
          </div>
          <Button type="submit" size="sm" disabled={!clientId.trim() || !clientSecret.trim() || save.isPending}>
            {save.isPending && <Loader2 className="size-3.5 animate-spin" />}
            Save client
          </Button>
          {save.isError && <InlineError error={save.error} />}
        </form>
      )}
    </ConnectionSection>
  );
}

// What the granted scopes actually reach, from the backend's probe: a count
// and a few examples per app.
function WhatBooCanSee({
  visibility,
}: {
  visibility: { items: Record<string, { count: number; examples: ReadonlyArray<string>; capped: boolean }>; errors: Record<string, string> };
}) {
  const probes = GOOGLE_WORKSPACE_CAPABILITIES.flatMap((cap) => {
    const probe = cap.visibilityKey ? visibility.items[cap.visibilityKey] : undefined;
    return probe ? [{ cap, probe }] : [];
  });
  const errors = Object.entries(visibility.errors);
  if (probes.length === 0 && errors.length === 0) return null;
  return (
    <ConnectionSection title="What Boo can see" description="What the granted access reaches right now.">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {probes.map(({ cap, probe }) => (
          <div key={cap.id} className="rounded-lg border p-3">
            <div className="text-xs text-muted-foreground">{cap.name}</div>
            <div className="text-base font-medium tabular-nums">
              {probe.count}
              {probe.capped ? "+" : ""}
            </div>
            {probe.examples.length > 0 && (
              <div className="truncate text-xs text-muted-foreground">
                {probe.examples.slice(0, 2).join(", ")}
                {probe.examples.length > 2 ? "…" : ""}
              </div>
            )}
          </div>
        ))}
      </div>
      {errors.length > 0 && (
        <p className="text-xs text-amber-700 dark:text-amber-300">
          Couldn&apos;t check: {errors.map(([k, v]) => `${k}: ${v}`).join("; ")}
        </p>
      )}
    </ConnectionSection>
  );
}
